import { ComboboxItem } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { Search } from 'lucide-react';

import StyledMultiSelect from '@/components/StyledMultiSelect.tsx';

import { useAutoSelectSoleOption } from '@/hooks/use-auto-select-sole-option.ts';

import { TaskFormValues } from '@/models/TaskFormValues.ts';

interface Props {
    form: UseFormReturnType<TaskFormValues>;
    options: ComboboxItem[];
    /** Existing tasks are never filled in for the user — see the note on auto-selection below. */
    isEditing: boolean;
}

/**
 * Betriebsstätten picker. Lives in its own component because the sole option can only be selected
 * once the practices have arrived from SWR, which is after Mantine has frozen the form's
 * initialValues — so it takes an effect, and an effect needs the form the render prop hands down.
 *
 * Auto-selection is limited to new tasks. On an existing task it would write a Betriebsstätte the
 * task never had — an edit made to change something else would silently gain one — so a task
 * saved without one (created before the field was mandatory) has to be filled in deliberately.
 */
export const TaskLocationsSelect = ({ form, options, isEditing }: Props) => {
    const { locations, instance_id: instanceId } = form.values;

    const handleLocationsChange = (value: string[] | null): void => {
        form.setFieldValue('locations', value ?? []);
    };

    useAutoSelectSoleOption({
        options: options,
        getKey: (option) => option.value,
        hasSelection: locations.length > 0,
        onSelect: (option) => handleLocationsChange([option.value]),
        isEnabled: !!instanceId && !isEditing,
    });

    return (
        <StyledMultiSelect
            withAsterisk
            label="Betriebsstätten"
            placeholder="Betriebsstätten wählen"
            data={options}
            searchable
            leftSection={<Search className="size-4" />}
            value={locations}
            onChange={handleLocationsChange}
            disabled={!instanceId}
            error={locations.length === 0 ? 'Bitte wählen Sie mindestens eine Betriebsstätte aus.' : undefined}
        />
    );
};
